import {
  HttpClient,
  HttpErrorResponse,
  HttpParams
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FacilitySubscriptionProduct,
  FacilitySubscriptionStatus,
  InvoiceStatus,
  FacilitySubscriptionResponse,
  OpenCycleSubscription,
  PagedResultDto,
  SubscriptionInvoice,
  SubscriptionInvoiceShift,
  FacilitySubscriptionSummary,
  toSubscriptionProducts,
  toSubscriptionSummary,
  warnOnTierPriceDrift
} from '@umss/shared/data-access';
import { HttpHelper, ShiftStatusEnum } from '@umss/shared/utils';
import {
  catchError,
  map,
  Observable,
  OperatorFunction,
  of,
  tap,
  throwError
} from 'rxjs';

/**
 * Query parameters for the subscription invoices list.
 *
 * There is no free-text search: the API filters by status and by cycle
 * *overlap* with a date range, nothing else.
 */
export interface SubscriptionInvoiceSearch {
  status?: InvoiceStatus;
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** Paging for the shifts behind an invoice. The API takes no other filter. */
export interface SubscriptionInvoiceShiftSearch {
  pageNumber?: number;
  pageSize?: number;
}

/**
 * A 404 from the subscription routes means "this facility has never
 * subscribed" — the routine free-tier case, and the signal to render the
 * subscribe CTA. It must never surface as an error.
 *
 * The status is overloaded: an unknown facility id answers the same way. So a
 * 404 says only "no subscription to show", never that the facility is absent.
 */
function neverSubscribed<T, F>(fallback: F): OperatorFunction<T, T | F> {
  return catchError<T, Observable<F>>((error: unknown) =>
    error instanceof HttpErrorResponse && error.status === 404
      ? of(fallback)
      : throwError(() => error)
  );
}

/**
 * Facility subscription data.
 *
 * Three contract points worth keeping in mind:
 *  - the subscription `GET` answers **404 when the facility has never
 *    subscribed**. That is the routine free-tier case and must not surface as
 *    an error; callers treat it as "no subscription", not a failure.
 *  - reads are gated server-side by facility access, not by a permission.
 *    Mutations require `manage_subscriptions`, so those controls are shown
 *    read-only rather than offered and then rejected.
 *  - the shifts list is queried live while the invoice's `countableShifts` is
 *    frozen at cycle close. Corrected shift data can make them differ, so the
 *    row count must not be asserted against it.
 */
@Injectable({ providedIn: 'root' })
export class FacilitySubscriptionService {
  private readonly http = inject(HttpClient);

  private baseUrl(facilityId: string): string {
    return `api/facilities/${facilityId}/subscription`;
  }

  /**
   * How many shifts are scheduled beyond a date — what a facility stands to
   * lose by switching Flex Shifts off.
   *
   * Counts the shifts the facility would see on its own shifts page, so the
   * number in the warning is one they can go and check. Cancelled and finished
   * shifts are excluded: they are not at risk. Only the total is read, so this
   * asks for a single row rather than the list.
   */
  countShiftsAfter(facilityId: string, date: string): Observable<number> {
    const params = new HttpParams({
      fromObject: {
        facilityId,
        startDate: date,
        pageNumber: 1,
        pageSize: 1
      }
    }).appendAll({
      statuses: [
        ShiftStatusEnum.Posted,
        ShiftStatusEnum.Pending,
        ShiftStatusEnum.PartiallyFilled,
        ShiftStatusEnum.Confirmed
      ]
    });

    return this.http
      .get<PagedResultDto<unknown>>('api/shift-needs', { params })
      .pipe(
        map((result) => result.totalCount ?? 0),
        // A count is decoration on the warning — failing to get it must not
        // stop the facility unsubscribing.
        catchError(() => of(0))
      );
  }

  /**
   * The open cycle as a live, tax-exclusive estimate.
   *
   * 404s when there is no active subscription or no current cycle.
   */
  getOpenCycle(facilityId: string): Observable<OpenCycleSubscription | null> {
    return this.http
      .get<OpenCycleSubscription>(`${this.baseUrl(facilityId)}/open-cycle`)
      .pipe(
        // The apps display their own ladder; this is the only place the API's
        // copy is visible, so it is checked rather than ignored.
        tap((cycle) => warnOnTierPriceDrift(cycle.bands)),
        neverSubscribed(null)
      );
  }

  /**
   * Active state of every subscription product for the facility.
   *
   * Read off the subscription record rather than a dedicated route. A facility
   * that has never subscribed 404s, which resolves to no active products —
   * the free-tier state, not a failure.
   *
   * TODO(BE): the API carries no per-product price, so the Job Postings
   * "$100 / mo" line has no source and is hidden. Ask for it on
   * `SubscriptionProductDto` — the figure is business data the API should own,
   * and it is already implied by the ProductFee invoice line.
   */
  getSubscription(
    facilityId: string
  ): Observable<FacilitySubscriptionSummary | null> {
    return this.http
      .get<FacilitySubscriptionResponse>(this.baseUrl(facilityId))
      .pipe(
        map(toSubscriptionSummary),
        neverSubscribed<FacilitySubscriptionSummary, null>(null)
      );
  }

  /**
   * Turns a product on or off.
   *
   * Switching **on** goes through activation rather than the products route.
   * `POST …/products/{product}` requires the subscription to already exist and
   * 404s otherwise, so a facility subscribing for the first time could never
   * use it. Activation creates the subscription where there is none and adds
   * to it where there is — and it only ever switches the listed products on,
   * never off, so naming one cannot disturb the other.
   *
   * Switching **off** uses the products route, which is safe: a product can
   * only be turned off if a subscription exists to hold it. Deactivation takes
   * effect at the cycle end and the product stays usable until then.
   */
  setSubscriptionActive(
    facilityId: string,
    product: FacilitySubscriptionProduct,
    active: boolean
  ): Observable<FacilitySubscriptionStatus> {
    // Both routes answer with the whole subscription. The product is read back
    // out of it rather than assumed from what was asked: switching off takes
    // effect at the cycle boundary, so the answer carries an end date and stays
    // entitled until it — assuming `{ active: false }` here revokes it at once
    // and stops the facility scheduling shifts it has already paid for.
    const request = active
      ? this.http.post<FacilitySubscriptionResponse>(this.baseUrl(facilityId), {
          products: [product]
        })
      : this.http.delete<FacilitySubscriptionResponse>(
          `${this.baseUrl(facilityId)}/products/${product}`
        );

    return request.pipe(
      map(
        (response) =>
          toSubscriptionProducts(response).find(
            (status) => status.product === product
          ) ?? { product, active }
      )
    );
  }

  /** Paged subscription invoices, newest cycle first. Empty page, never 404. */
  getInvoices(
    facilityId: string,
    search: SubscriptionInvoiceSearch = {}
  ): Observable<PagedResultDto<SubscriptionInvoice>> {
    return this.http.get<PagedResultDto<SubscriptionInvoice>>(
      `${this.baseUrl(facilityId)}/invoices`,
      { params: HttpHelper.toHttpParams(search) }
    );
  }

  /** One invoice with its lines. */
  getInvoice(
    facilityId: string,
    invoiceId: string
  ): Observable<SubscriptionInvoice> {
    return this.http.get<SubscriptionInvoice>(
      `${this.baseUrl(facilityId)}/invoices/${invoiceId}`
    );
  }

  /**
   * The shifts counted toward this invoice's tier, paged.
   *
   * Queried live while the invoice's `countableShifts` is frozen at cycle
   * close, so the two can legitimately differ once shift data is corrected.
   * Includes facility-initiated late cancellations, which consumed the booking
   * and count — those carry no clock times.
   */
  getInvoiceShifts(
    facilityId: string,
    invoiceId: string,
    search: SubscriptionInvoiceShiftSearch = {}
  ): Observable<PagedResultDto<SubscriptionInvoiceShift>> {
    return this.http.get<PagedResultDto<SubscriptionInvoiceShift>>(
      `${this.baseUrl(facilityId)}/invoices/${invoiceId}/shifts`,
      { params: HttpHelper.toHttpParams(search) }
    );
  }
}
